/**
 * Button
 *
 * Design system inspiration: Linear — flat aesthetic, font-weight 400 (light),
 * no shadows, subtle scale-down on press, tight padding.
 *
 * Dependencies: @/lib/utils (cn)
 *
 * Copy this file to src/components/ui/Button.tsx
 */

import React from "react"
import { cn } from "@/lib/utils"

// ── Genome sources ────────────────────────────────────────────────────────────
// Block:    blocks/Button/meta.yaml
// Tokens:   genome/rules/styling-tokens.rule.md
// Safety:   safety/hard-constraints.md rules 14, 18, 20
//
// INVARIANTS (meta.yaml):
//   rounded-md border radius
//   whitespace-nowrap — label never wraps (rule 18)
//   focus-visible:ring-2 focus-visible:ring-offset-1 (rule 20)
//   font-normal (400 weight)
//   transition-[color,background-color,border-color,transform] duration-100
//
// Rule 14: only one primary CTA visible per surface.

type ButtonVariant = "primary" | "destructive" | "basic" | "outline" | "transparent" | "link"
type StandardSize = "sm" | "default" | "lg"
type LinkSize = "sm" | "default"

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  primary:     "bg-primary text-primary-foreground font-light hover:bg-primary-hover active:bg-primary-active focus-visible:ring-ring",
  destructive: "bg-destructive text-destructive-foreground font-light hover:bg-destructive-hover active:bg-destructive-active focus-visible:ring-ring-destructive",
  basic:       "bg-muted text-foreground font-light hover:bg-muted-hover active:bg-muted-active focus-visible:ring-ring",
  outline:     "border border-border bg-transparent text-foreground font-light hover:bg-muted-hover active:bg-muted-active focus-visible:ring-ring",
  transparent: "bg-transparent text-muted-foreground font-light hover:bg-muted-hover hover:text-foreground active:bg-muted-active focus-visible:ring-ring",
  link:        "bg-transparent text-primary font-normal underline-offset-2 hover:underline hover:text-primary-hover active:text-primary-active focus-visible:ring-ring",
}

const OUTLINE_SELECTED_CLASSES = "border-primary bg-accent text-accent-foreground"

const SIZE_CLASSES: Record<StandardSize, string> = {
  sm:      "h-6 px-1.5 text-sm [&_svg]:size-3",
  default: "h-8 px-2.5 text-base [&_svg]:size-4",
  lg:      "h-10 px-3.5 text-lg [&_svg]:size-5",
}

const ICON_SIZE_CLASSES: Record<StandardSize, string> = {
  sm:      "h-6 w-6 p-0 [&_svg]:size-3",
  default: "h-8 w-8 p-0 [&_svg]:size-4",
  lg:      "h-10 w-10 p-0 [&_svg]:size-5",
}

type ButtonPropsBase = {
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  asChild?: boolean
  selected?: boolean
  className?: string
} & React.ButtonHTMLAttributes<HTMLButtonElement>

type StandardButtonProps = ButtonPropsBase & {
  variant?: Exclude<ButtonVariant, "link">
  size?: StandardSize
  iconOnly?: boolean
}

type LinkButtonProps = ButtonPropsBase & {
  variant: "link"
  size?: LinkSize
  iconOnly?: never
}

type ButtonProps = StandardButtonProps | LinkButtonProps

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      variant = "primary",
      size = "default",
      leftIcon,
      rightIcon,
      iconOnly = false,
      asChild: _asChild = false,
      selected = false,
      className,
      children,
      ...props
    },
    ref,
  ) {
    const effectiveSize: StandardSize = variant === "link" && size === "lg" ? "default" : size
    const effectiveIconOnly = variant === "link" ? false : iconOnly

    return (
      <button
        ref={ref}
        aria-pressed={selected || undefined}
        className={cn(
          "inline-flex items-center justify-center gap-1.5 select-none",
          "rounded-md whitespace-nowrap",
          "transition-[color,background-color,border-color,transform] duration-100",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1",
          "disabled:pointer-events-none disabled:opacity-50",
          VARIANT_CLASSES[variant],
          selected && variant === "outline" && OUTLINE_SELECTED_CLASSES,
          effectiveIconOnly ? ICON_SIZE_CLASSES[effectiveSize] : SIZE_CLASSES[effectiveSize],
          className,
        )}
        {...props}
      >
        {effectiveIconOnly ? (
          children
        ) : (
          <>
            {leftIcon}
            {children}
            {rightIcon}
          </>
        )}
      </button>
    )
  },
)

Button.displayName = "Button"
